import { Controller } from 'stimulus'
import { getIs24h, setIs24h } from '~/controllers/booking/time_format'
import {
  matchBrowserZone,
  pickIsRecent,
  readPick,
  rememberedZone,
  writePick,
} from './time_zone_select_browser_zone'

export default class extends Controller {
  static targets = [
    'hiddenField',
    'opener',
    'currentTime',
    'search',
    'row',
    'zones',
    'headingTemplate',
    'rowTemplate',
  ]
  static values = {
    selectedTz: String,
    ampm: { type: Boolean, default: true },
    persistTimeFormat: { type: Boolean, default: false },
    // The server only guessed the zone from the IP: prefer the device's zone.
    browserDefault: { type: Boolean, default: false },
    rememberPick: { type: Boolean, default: false },
    // The value is the admin's profile zone: note a device in another zone, once per element.
    deviceNoteOnMismatch: { type: Boolean, default: false },
  }

  connect() {
    this.buildRows()
    const iana = this.hiddenFieldTarget.value
    if (iana) {
      this.selectedTzValue = iana
      this.updateSelectedRow(iana)
    }
    this.hiddenFieldTarget.addEventListener('change', this.syncFromHiddenField)
    this.markDeviceRow()
    // A restored (Turbo-cached) page keeps a visible note but loses its click handler: bind on every connect.
    this.bindDeviceNoteButtons()
    // Once per element: FullCalendar moves the header select into its toolbar, which reconnects this
    // controller, and a cached page reconnects it too. Neither may undo a pick (the marker survives both).
    if (this.browserDefaultValue && !this.element.dataset.tzBrowserApplied)
      this.applyBrowserTimeZone()
    else if (
      this.deviceNoteOnMismatchValue &&
      !this.element.dataset.tzMismatchNoted
    )
      this.noteDeviceMismatch()
    // Neither the opener's "3:15 PM now" nor the list's clocks and offsets should go stale while the page sits open.
    this.nowTimer = setInterval(() => this.updateAllTimes(), 30_000)
    // Re-fit whenever the pill's room changes. The observer catches it being squeezed (or FullCalendar
    // moving it into the toolbar); a content-sized pill does not grow when room returns, so the
    // window listener covers widening.
    this.fitObserver = new ResizeObserver(this.fitOpeners)
    this.openerTargets.forEach((opener) => this.fitObserver.observe(opener))
    window.addEventListener('resize', this.fitOpeners)
    this.fitOpeners()
    document.fonts?.ready.then(this.fitOpeners)

    if (this.persistTimeFormatValue) {
      // The 24h toggle's initial state is driven by the shared preference
      // (localStorage override -> account default). Keep this and the sibling
      // toggle (desktop vs mobile) in sync via the time-format:change event.
      this.syncFromGlobalPreference()
      document.addEventListener('time-format:change', this.onTimeFormatChange)
      // A capture-phase click on the switch is the only signal that a real
      // visitor (not a boot/reconnect) moved it. See toggleFormat.
      this.switchElement?.addEventListener('click', this.markUserToggle, true)
    }
  }

  disconnect() {
    clearInterval(this.nowTimer)
    this.fitObserver?.disconnect()
    window.removeEventListener('resize', this.fitOpeners)
    this.hiddenFieldTarget.removeEventListener(
      'change',
      this.syncFromHiddenField
    )
    if (this.persistTimeFormatValue) {
      document.removeEventListener(
        'time-format:change',
        this.onTimeFormatChange
      )
      this.switchElement?.removeEventListener(
        'click',
        this.markUserToggle,
        true
      )
    }
  }

  swallow() {
    // no-op
  }

  // The server sends the zone list and one prototype row and heading instead of ~540 rendered rows (about 750 KB).
  // Everything below reads the rows, so they are built first. A page restored from the Turbo cache, or a select
  // FullCalendar moved into its toolbar, reconnects with its rows already in place.
  buildRows() {
    if (this.hasRowTarget || !this.hasZonesTarget || !this.hasRowTemplateTarget)
      return
    const zones = JSON.parse(this.zonesTarget.textContent)
    const heading = this.headingTemplateTarget.content.querySelector('li')
    const prototype = this.rowTemplateTarget.content.querySelector('li')
    const clock = this.clockFormat('UTC')
    const now = Date.now()
    const rows = document.createDocumentFragment()
    zones.forEach(([region, regionZones]) => {
      const headingItem = heading.cloneNode(true)
      headingItem.querySelector('[data-menu-label]').textContent = region
      rows.append(headingItem)
      regionZones.forEach(([iana, name, offset]) => {
        const item = prototype.cloneNode(true)
        item.dataset.searchValue = [name, region, iana]
          .join(' ')
          .toLowerCase()
          .replace(/[/_]/g, ' ')
        const row = item.querySelector(
          '[data-form--time-zone-select-target="row"]'
        )
        Object.assign(row.dataset, {
          offset,
          iana,
          displayName: name,
          name: name.toLowerCase(),
        })
        item.querySelector('[data-menu-label]').textContent = name
        // The zone's clock is UTC shifted by its offset; updateAllTimes (on open, every 30 s) re-derives both per zone.
        item.querySelector(
          '[data-form--time-zone-select-target="currentTime"]'
        ).textContent = clock.format(new Date(now + offset * 1000))
        item.querySelector('[data-tz-row-offset]').textContent =
          this.offsetLabel(offset)
        rows.append(item)
      })
    })
    this.rowTemplateTarget.before(rows)
  }

  // "Your device" on the row that matches the browser's zone, whichever zone is selected.
  markDeviceRow() {
    const iana = matchBrowserZone(
      this.rowTargets.map((row) => row.dataset.iana)
    )
    if (!iana) return
    this.rowTargets
      .find((row) => row.dataset.iana === iana)
      ?.querySelector('[data-tz-device-label]')
      ?.classList.remove('hidden')
  }

  updateOpenerNow() {
    const iana = this.selectedTzValue
    if (!iana) return
    this.element.querySelectorAll('[data-tz-now]').forEach((el) => {
      el.textContent = this.formatCurrentTime(iana)
    })
    // The offset follows the clock so a page left open across a DST change stays right.
    this.element.querySelectorAll('[data-tz-offset]').forEach((el) => {
      el.textContent = this.formatOffset(iana)
    })
    // A longer clock ("9:59 PM" -> "10:00 PM") or a 12h/24h switch can change what fits.
    this.fitOpeners()
  }

  // "GMT-7" / "GMT+5:30" from an offset in seconds, same shape as the server's offset_label.
  offsetLabel(seconds) {
    const minutesTotal = Math.round(Math.abs(seconds) / 60)
    const hours = Math.floor(minutesTotal / 60)
    const minutes = minutesTotal % 60
    const sign = seconds < 0 ? '-' : '+'
    return `GMT${sign}${hours}${
      minutes ? `:${String(minutes).padStart(2, '0')}` : ''
    }`
  }

  // "GMT-7" / "GMT+5:30", same shape as the server's offset_label, computed for right now.
  formatOffset(iana) {
    const name = new Intl.DateTimeFormat('en-US', {
      timeZone: iana,
      timeZoneName: 'shortOffset',
    })
      .formatToParts(new Date())
      .find((part) => part.type === 'timeZoneName')?.value
    return name === 'GMT' ? 'GMT+0' : name || ''
  }

  // Order of precedence: a zone the visitor picked, then the browser zone, then the
  // server's IP guess that is already in the field. Dispatches change like a pick so
  // the calendar, the sibling picker and the booking form all follow.
  applyBrowserTimeZone() {
    this.element.dataset.tzBrowserApplied = '1'
    const ianas = this.rowTargets.map((row) => row.dataset.iana)
    const device = matchBrowserZone(ianas)
    // A pick remembered from an earlier visit beats the device zone, as long as the device hasn't moved.
    // Only booking pages store a pick. An admin calendar (no remember_pick) must not inherit one.
    const pick = this.rememberPickValue ? readPick() : null
    const remembered = rememberedZone(pick, ianas, device)
    const iana = remembered || device
    if (iana && iana !== this.hiddenFieldTarget.value) this.applyZone(iana)
    if (remembered && device && remembered !== device)
      this.showDeviceNote(device, { loud: !pickIsRecent(pick) })
  }

  // Admin calendars show the admin's profile zone; a device elsewhere (travel, a laptop clock set to
  // another zone) gets the quiet note. Switching applies to this page only (nothing is remembered).
  noteDeviceMismatch() {
    this.element.dataset.tzMismatchNoted = '1'
    const device = matchBrowserZone(
      this.rowTargets.map((row) => row.dataset.iana)
    )
    if (device && device !== this.hiddenFieldTarget.value)
      this.showDeviceNote(device)
  }

  applyZone(iana) {
    const row = this.rowTargets.find((r) => r.dataset.iana === iana)
    if (!row) return
    this.selectedTzValue = iana
    this.hiddenFieldTarget.value = iana
    this.hiddenFieldTarget.dispatchEvent(new Event('change', { bubbles: true }))
    this.updateOpenerLabels(row.dataset.displayName)
    this.updateSelectedRow(iana)
  }

  // "Your device is set to Los Angeles. Use Los Angeles time." under the opener: the remembered pick stays
  // unless the visitor takes the offer. A pick from the last few minutes gets the quiet line; an older
  // one, which the visitor may have forgotten about, gets the framed version.
  showDeviceNote(device, { loud = false } = {}) {
    this.hideDeviceNote()
    const note = this.noteRoot.querySelector(
      `[data-tz-device-note="${loud ? 'loud' : 'quiet'}"]`
    )
    const city = this.rowTargets.find((r) => r.dataset.iana === device)?.dataset
      .displayName
    if (!note || !city) return
    note.querySelectorAll('[data-template]').forEach((el) => {
      el.textContent = el.dataset.template.replace('%{city}', city)
    })
    note.classList.remove('hidden')
    this.positionDeviceNote()
  }

  // The note may sit outside this controller's element (calendar header), so the buttons are wired directly.
  bindDeviceNoteButtons() {
    this.noteRoot
      .querySelectorAll('[data-tz-device-note] button')
      .forEach((button) => (button.onclick = () => this.useDeviceZone()))
  }

  hideDeviceNote() {
    this.noteRoot
      .querySelectorAll('[data-tz-device-note]')
      .forEach((note) => note.classList.add('hidden'))
  }

  // A calendar header renders the note under its toolbar rather than under the (cramped) opener.
  get noteRoot() {
    return this.element.closest('.calendar-component') || this.element
  }

  // Under the toolbar the note sits centered under the pill, kept inside the row.
  positionDeviceNote() {
    if (this.noteRoot === this.element) return
    const note = this.noteRoot.querySelector(
      '[data-tz-device-note]:not(.hidden)'
    )
    const pill = this.openerTargets.find((opener) => opener.offsetParent)
    if (!note || !pill) return
    const row = note.parentElement.getBoundingClientRect()
    const p = pill.getBoundingClientRect()
    const wanted = p.left + p.width / 2 - note.offsetWidth / 2 - row.left
    const left = Math.min(Math.max(wanted, 0), row.width - note.offsetWidth)
    note.style.marginLeft = `${Math.round(left)}px`
  }

  useDeviceZone() {
    const device = matchBrowserZone(
      this.rowTargets.map((row) => row.dataset.iana)
    )
    if (!device) return
    this.rememberPick(device)
    this.applyZone(device)
    this.hideDeviceNote()
  }

  // Only the booking-page selects (remember_pick) store a pick per browser.
  rememberPick(iana) {
    if (!this.rememberPickValue) return
    writePick(
      iana,
      matchBrowserZone(this.rowTargets.map((row) => row.dataset.iana))
    )
  }

  selectTimezone(e) {
    const row = e.currentTarget
    const iana = row.dataset.iana
    const displayName = row.dataset.displayName

    if (this.hiddenFieldTarget.value === iana) return

    // Set selected value before dispatching change so syncFromHiddenField
    // sees it as already applied and returns early
    this.selectedTzValue = iana
    this.element.dataset.tzBrowserApplied = '1'
    this.rememberPick(iana)
    this.hideDeviceNote()

    // Update hidden field and dispatch change
    this.hiddenFieldTarget.value = iana
    this.hiddenFieldTarget.dispatchEvent(new Event('change', { bubbles: true }))

    // Sync to any bottom registration form's hidden timezone field
    this.syncBottomRegistrationTimezone(iana)

    // Update opener labels (desktop + mobile may each have one)
    this.updateOpenerLabels(displayName)
    this.updateSelectedRow(iana)
  }

  syncBottomRegistrationTimezone(iana) {
    // Find the bottom event registration form's hidden timezone field
    const bottomTzField = document.querySelector('[id^="bottom_tz_field_"]')
    if (bottomTzField) {
      bottomTzField.value = iana
    }
  }

  // Called when an external source (e.g. time_zone_syncer) changes the hidden
  // field value. Updates the opener label and row highlight to stay in sync.
  syncFromHiddenField = () => {
    const iana = this.hiddenFieldTarget.value
    if (iana === this.selectedTzValue) return

    this.selectedTzValue = iana
    this.updateSelectedRow(iana)
    // The visitor acted on a sibling select (desktop pill vs. phone pill): this note is stale.
    this.hideDeviceNote()

    const row = this.rowTargets.find((r) => r.dataset.iana === iana)
    if (row) this.updateOpenerLabels(row.dataset.displayName)
  }

  // The list opens scrolled to the current zone, so the visitor sees what is selected.
  // Waits for the dropdown's open animation; rects measured mid-scale land in the wrong place.
  revealSelected() {
    // The list may have been mounted a while ago: bring its clocks and offsets up to date as it opens.
    this.updateAllTimes()
    setTimeout(() => {
      const row = this.rowTargets.find(
        (r) => r.dataset.iana === this.selectedTzValue
      )
      const scroller = row?.closest('[data-tz-scroller]')
      if (!row || !scroller) return
      const rowRect = row.getBoundingClientRect()
      const scrollerRect = scroller.getBoundingClientRect()
      scroller.scrollTop +=
        rowRect.top -
        scrollerRect.top -
        (scrollerRect.height - rowRect.height) / 2
    }, 250)
  }

  toggleFormat(e) {
    this.ampmValue = !e.detail.active
    this.updateAllTimes()

    if (!this.persistTimeFormatValue) return

    // Only a real click pins the preference. The nested switch fires
    // `switch:change` on every (re)connect — move-element relocates its pane on
    // boot, reconnecting it — and those dispatches just re-state the current DOM
    // state. Persisting them would pin the account default into localStorage
    // before the visitor actually toggles. markUserToggle (capture phase) is the
    // only thing that flips userToggledFormat, and it fires ahead of the
    // switch's own click action, so a boot dispatch is never mistaken for one.
    if (!this.userToggledFormat) {
      this.syncSwitchVisual()
      return
    }
    this.userToggledFormat = false
    // e.detail.active === true means the switch is "on" = 24h (on_value: '24h').
    if (e.detail.active !== getIs24h()) setIs24h(e.detail.active)
  }

  markUserToggle = () => {
    this.userToggledFormat = true
  }

  // Align this toggle's preview + switch visual with the shared preference.
  // Used on connect and whenever the sibling toggle changes the preference.
  syncFromGlobalPreference() {
    this.ampmValue = !getIs24h()
    this.updateAllTimes()
    this.syncSwitchVisual()
  }

  onTimeFormatChange = () => {
    this.syncFromGlobalPreference()
  }

  // Flip the nested switch's DOM to match the shared preference WITHOUT going
  // through its controller (which would re-fire switch:change and cascade).
  get switchElement() {
    return this.element.querySelector('[data-controller~="switch"]')
  }

  syncSwitchVisual() {
    const switchEl = this.switchElement
    if (!switchEl) return

    const is24h = getIs24h()
    if (switchEl.classList.contains('active') === is24h) return

    switchEl.classList.toggle('active', is24h)
    const onField = switchEl.querySelector('[data-switch-target="onField"]')
    const offField = switchEl.querySelector('[data-switch-target="offField"]')
    if (onField) onField.checked = is24h
    if (offField) offField.checked = !is24h
  }

  // Row clocks and offsets follow the current moment, so a list opened across a DST change matches the pill.
  updateAllTimes() {
    this.currentTimeTargets.forEach((span) => {
      const row = span.closest('[data-iana]')
      if (!row) return

      span.textContent = this.formatCurrentTime(row.dataset.iana)
      const offset = row.querySelector('[data-tz-row-offset]')
      if (offset) offset.textContent = this.formatOffset(row.dataset.iana)
    })
    this.updateOpenerNow()
  }

  formatCurrentTime(iana) {
    return this.clockFormat(iana).format(new Date())
  }

  clockFormat(timeZone) {
    return new Intl.DateTimeFormat('en-US', {
      hour: !this.ampmValue ? '2-digit' : 'numeric',
      minute: !this.ampmValue ? '2-digit' : 'numeric',
      hour12: this.ampmValue,
      hourCycle: this.ampmValue ? 'h12' : 'h23',
      timeZone,
    })
  }

  // A "Times in <City> GMT-7 | 3:15 PM now" opener updates its parts; a plain one its text.
  updateOpenerLabels(displayName) {
    this.openerTargets.forEach((opener) => {
      const city = opener.querySelector('[data-tz-city]')
      if (city) {
        city.textContent = displayName
        return
      }
      const label = opener.querySelector('.t-btn-main-label')
      if (label) label.textContent = displayName
    })
    this.updateOpenerNow()
    this.fitOpeners()
  }

  // A "Times in <City> GMT-7 | 3:15 PM now" opener sheds its optional parts when it has no room:
  // first "Times in" and "now" (tier 1), then the GMT offset (tier 2), then the clock (tier 3),
  // so the city is the last thing to truncate.
  fitOpeners = () => {
    this.openerTargets.forEach((opener) => this.fitOpener(opener))
    this.positionDeviceNote()
  }

  fitOpener(opener) {
    const city = opener.querySelector('[data-tz-city]')
    const optional = [...opener.querySelectorAll('[data-tz-optional]')]
    if (!city || !optional.length) return
    optional.forEach((el) => el.classList.remove('hidden'))
    const truncated = () => city.scrollWidth > city.clientWidth
    for (const tier of ['1', '2', '3']) {
      if (!truncated()) return
      optional
        .filter((el) => el.dataset.tzOptional === tier)
        .forEach((el) => el.classList.add('hidden'))
    }
  }

  updateSelectedRow(iana) {
    this.rowTargets.forEach((row) => {
      const isSelected = row.dataset.iana === iana

      // Neutral tint plus a check: an accent fill made the text unreadable in both schemes.
      row.classList.toggle('bg-neutral-100', isSelected)
      row.classList.toggle('dark:bg-neutral-800', isSelected)

      const icon = row.querySelector('.js-check-icon')
      if (icon) icon.classList.toggle('invisible', !isSelected)
    })
  }
}
